import type { SignUp } from '../../../types/definitions/vendor/graphql-schema.d';
import { isNonEmptyString } from '../../../utils/helpers';
import { GlobalIdMe } from '../../../utils/requests/contentstack/contentStackZodSchema';

export const appendSignupType = (
  href: string,
  signupType: GlobalIdMe['signup_type'],
) => {
  if (isNonEmptyString(href) && isNonEmptyString(signupType)) {
    const queryParam = `signup_type=${encodeURIComponent(signupType)}`;

    return href.includes('?')
      ? `${href}&${queryParam}`
      : `${href}?${queryParam}`;
  }

  return href;
};

/**
 * Signup types related to ID.me registration:
 * [source](https://github.com/stitchfix/client-service/blob/ceaccd3b73edfdde66f3a5bbd3e3cd68324e02fb/app/models/client_signup_type.rb#L3)
 */
const ID_ME_REGISTRATION_SIGNUP_TYPES = [
  'idme_government_worker_onboarding',
  'idme_military_onboarding',
  'idme_nurse_onboarding',
  'idme_teacher_onboarding',
] as const;

export type IDMeRegistrationSignupType =
  (typeof ID_ME_REGISTRATION_SIGNUP_TYPES)[number];

export const isIDmeRegistrationSignupType = (
  signupType: unknown,
): signupType is IDMeRegistrationSignupType =>
  isNonEmptyString(signupType) &&
  ID_ME_REGISTRATION_SIGNUP_TYPES.includes(
    signupType as IDMeRegistrationSignupType,
  );

/**
 * The client's signupType value from the Graph
 */
export type IDmeClientSignUpType = Extract<
  SignUp,
  | 'IDME_GOVERNMENT_WORKER_ONBOARDING'
  | 'IDME_MILITARY_ONBOARDING'
  | 'IDME_NURSE_ONBOARDING'
  | 'IDME_TEACHER_ONBOARDING'
>;

export const isIDmeClientSignUpType = (
  signupType?: SignUp,
): signupType is IDmeClientSignUpType => {
  if (!signupType) return false;

  return [
    'IDME_GOVERNMENT_WORKER_ONBOARDING',
    'IDME_MILITARY_ONBOARDING',
    'IDME_NURSE_ONBOARDING',
    'IDME_TEACHER_ONBOARDING',
  ].includes(signupType as IDmeClientSignUpType);
};

const idMeCommunityRegex = /(government|military|nurse|teacher)/i;

export const hasIDmeCommunityName = (value: string) =>
  idMeCommunityRegex.test(value);
